/**
 * API Deployment Settings Schema
 *
 * Zod schema for Deployment.deploymentSettings when type = API.
 * Single source of truth for API deployment configuration.
 */

import { z } from "zod";

import { BaseSettingsSchema } from "./Base.schema";
import { VoiceConfigSchema } from "./VoiceConfig.schema";

// ============================================================
// VOICE SETTINGS (streaming voice for custom apps)
// ============================================================

/**
 * Realtime voice for API deployments: a custom app (e.g. a Vibe app) can open a
 * streaming voice session against this deployment via
 * `POST /public/v1/deployments/:deploymentId/voice-session`.
 *
 * Same shape as the embed widget's voiceSettings (shared VoiceConfig +
 * `enabled`) so both web surfaces flow through the same voice-relay context
 * builder.
 */
export const ApiVoiceSettingsSchema = VoiceConfigSchema.extend({
  /** Whether streaming voice sessions can be opened against this deployment. */
  enabled: z.boolean()
});

export type ApiVoiceSettings = z.infer<typeof ApiVoiceSettingsSchema>;

// ============================================================
// MAIN SCHEMA (Complete API Deployment Settings)
// ============================================================

/**
 * Complete API Deployment Settings Schema
 *
 * API deployments are minimal - they just expose the agent via API.
 * Most configuration is at the agent level.
 *
 * NOTE: agentId is NOT in deploymentSettings! It's a Deployment column (FK to Agent).
 */
export const ApiDeploymentSettingsSchema = z
  .object({
    /** Realtime voice for custom apps. Absent = voice disabled. */
    voiceSettings: ApiVoiceSettingsSchema.optional()
  })
  .merge(BaseSettingsSchema);

export type ApiDeploymentSettings = z.infer<typeof ApiDeploymentSettingsSchema>;

// ============================================================
// DEFAULTS (Single Source of Truth)
// ============================================================

/**
 * Default values for creating a new API deployment.
 */
export const DEFAULT_API_SETTINGS: ApiDeploymentSettings = {};

// ============================================================
// FACTORY FUNCTION (Type-Safe Creation)
// ============================================================

/**
 * Create complete API deployment settings with defaults.
 * Accepts unknown (e.g. raw Prisma JsonValue). Callers should follow up with Zod validation.
 */
export function createApiDeploymentSettings(overrides?: unknown): ApiDeploymentSettings {
  if (!overrides || typeof overrides !== "object" || Array.isArray(overrides)) {
    return { ...DEFAULT_API_SETTINGS };
  }
  return { ...DEFAULT_API_SETTINGS, ...overrides } as ApiDeploymentSettings;
}
